// POS-Restaurant → Resumen → Dashboard (/pos/rest-dashboard).
// Es el MISMO dashboard del POS (DashboardPos, tal cual: KPIs, comparativos
// hoy/mes/año, series, formas de pago, por caja, top productos) + una franja de
// cards PROPIOS del restaurante (rest_dashboard, 719/721): ventas por
// factura/nota de venta en hoy/mes/año, estado del salón y meseros del día.
import { useQuery } from '@tanstack/react-query';
import { ConciergeBell, DoorOpen, HandCoins, Users, UtensilsCrossed } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { DashboardPos, KpiTile } from './DashboardPos';
import { money, posRpc } from './shared';

interface RestDash {
  hoy: { cuentas: number; en_mesa: number; mostrador: number; total: number; ticket: number; servicio: number };
  ahora: { mesas_total: number; mesas_ocupadas: number; cuentas_abiertas: number; monto_abierto: number };
  meseros: { mesero: string; cuentas: number; total: number }[];
}

function CardsRestaurante() {
  const { data } = useQuery<RestDash>({
    queryKey: ['rest-dashboard'],
    queryFn: () => posRpc<RestDash>('rest_dashboard'),
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 60_000,
  });
  if (!data) return null;
  const { hoy, ahora, meseros } = data;
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
        <UtensilsCrossed className="w-4 h-4" /> Restaurante
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiTile t={{ icon: DoorOpen, label: 'Mesas ocupadas', valor: `${ahora.mesas_ocupadas} / ${ahora.mesas_total}`, sub: `${Number(ahora.cuentas_abiertas)} cuenta(s) abiertas` }} />
        <KpiTile t={{ icon: HandCoins, label: 'Abierto por cobrar', valor: money(Number(ahora.monto_abierto)), sub: 'líneas de cuentas abiertas (sin 10% ni IVA)' }} />
        <KpiTile t={{ icon: ConciergeBell, label: 'Servicio 10% hoy', valor: money(Number(hoy.servicio)), sub: `mesa ${hoy.en_mesa} · mostrador ${hoy.mostrador}` }} />
      </div>

      {meseros.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Meseros de hoy</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm max-w-xl">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3">Mesero</th><th className="py-1.5 pr-3 text-right">Cuentas</th><th className="py-1.5 text-right">Total</th>
              </tr></thead>
              <tbody>{meseros.map((m) => (
                <tr key={m.mesero} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{m.mesero}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{m.cuentas}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium">{money(Number(m.total))}</td>
                </tr>))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function RestDashboard() {
  return <DashboardPos titulo="Dashboard del restaurante" modo="restaurant" extra={<CardsRestaurante />} />;
}
